/**
 * geo.js — Geocoding + Pacific-time helpers for the Open House Planner.
 *
 * geocodeAddress(address) — window.google.maps.Geocoder biased to the
 *   Santa Barbara area; resolves {lat, lng} or throws.
 * toPtIso(dateStr, timeStr) — 'YYYY-MM-DD' + 'h:mm AM/PM' wall time in
 *   America/Los_Angeles → UTC ISO string (DST-correct via Intl offsets).
 */

const PT_ZONE = 'America/Los_Angeles';

// Default starting point for every day plan (Cyprian's home base). Coordinates
// are hardcoded because the browser Geocoding API isn't enabled on the project.
export const DEFAULT_PLAN_START = {
  address: '550 Asilomar Way, Goleta, CA 93111',
  lat: 34.4315,
  lng: -119.8221,
};

// Rough South County bounding box (Goleta → Carpinteria) around 34.42,-119.70
const SB_BOUNDS = {
  south: 34.30,
  west: -120.05,
  north: 34.55,
  east: -119.40,
};

function waitForGeocoder(attempts = 0) {
  return new Promise((resolve, reject) => {
    const check = (n) => {
      if (window.google?.maps?.Geocoder) return resolve();
      if (n > 60) return reject(new Error('Google Maps not loaded'));
      setTimeout(() => check(n + 1), 100);
    };
    check(attempts);
  });
}

/**
 * Geocode a street address, biased to Santa Barbara.
 * @param {string} address
 * @returns {Promise<{lat: number, lng: number}>}
 */
export async function geocodeAddress(address) {
  if (!address || !address.trim()) throw new Error('No address to geocode');
  await waitForGeocoder();
  const geocoder = new window.google.maps.Geocoder();
  const bounds = new window.google.maps.LatLngBounds(
    { lat: SB_BOUNDS.south, lng: SB_BOUNDS.west },
    { lat: SB_BOUNDS.north, lng: SB_BOUNDS.east },
  );
  return new Promise((resolve, reject) => {
    geocoder.geocode({ address: address.trim(), bounds }, (results, status) => {
      const loc = results?.[0]?.geometry?.location;
      if (status === 'OK' && loc) {
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else {
        reject(new Error(`Geocoding failed (${status})`));
      }
    });
  });
}

/** '1:30 PM' / '1 PM' → {h: 13, min: 30} (24h). Throws on unparseable input. */
function parseTime12(timeStr) {
  const m = /^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i.exec(String(timeStr || '').trim());
  if (!m) throw new Error(`Unrecognized time: "${timeStr}"`);
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const pm = m[3].toUpperCase() === 'PM';
  if (h < 1 || h > 12 || min > 59) throw new Error(`Unrecognized time: "${timeStr}"`);
  if (h === 12) h = pm ? 12 : 0;
  else if (pm) h += 12;
  return { h, min };
}

/** Offset (zone-local minus UTC) in ms at a given UTC instant. */
function tzOffsetMs(utcMs, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = {};
  for (const p of dtf.formatToParts(new Date(utcMs))) parts[p.type] = p.value;
  const asUtc = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    parseInt(parts.hour, 10) % 24, // hour12:false can yield "24" at midnight
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10),
  );
  return asUtc - utcMs;
}

/**
 * Build a UTC ISO string for a Pacific wall-clock time (DST-correct).
 * @param {string} dateStr - 'YYYY-MM-DD'
 * @param {string} timeStr - 'h:mm AM/PM' (minutes optional, e.g. '1 PM')
 * @returns {string} ISO timestamp
 */
export function toPtIso(dateStr, timeStr) {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || '').trim());
  if (!dm) throw new Error(`Unrecognized date: "${dateStr}"`);
  const { h, min } = parseTime12(timeStr);
  // Pretend the wall time is UTC, then subtract the zone offset at that
  // instant (iterated once so DST transitions resolve correctly).
  const wallUtc = Date.UTC(+dm[1], +dm[2] - 1, +dm[3], h, min, 0);
  let offset = tzOffsetMs(wallUtc, PT_ZONE);
  offset = tzOffsetMs(wallUtc - offset, PT_ZONE);
  return new Date(wallUtc - offset).toISOString();
}
